import React, { FC } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetLocationData } from "~/hooks/locationData";
import { onScrollBottomSubdivision } from "~/hooks/selectSubdivisionValue";
import { callEnumVal } from "~/libs/callEnumVal";
import {
  useAddViolationTypeMutation,
  useChangeStatusViolationTypeMutation,
  useEditViolationTypeMutation,
  ViolationTypeCategory,
  ViolationTypeStatusChange,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { Field } from "~/shared/ui/Field";

interface ViolationTypesFormProps {
  setCloseModal: () => void;
  refetch: () => void;
  violation;
  hasPermission: (key: string) => boolean;
  formId: string;
}

const ViolationTypesForm: FC<ViolationTypesFormProps> = ({
  setCloseModal,
  refetch,
  violation,
  hasPermission,
  formId,
}) => {
  const { t } = useTranslation();
  const { openModal } = useGetLocationData();
  const isEdit = openModal === "Edit";

  const {
    divisionsForSelect,
    onScrollBottomDivisions,
    searchDivision,
    GetSubdivisionLoading,
  } = onScrollBottomSubdivision();

  const { category, SubdivisionId } = violation || {};

  const categoryOptions = Object.values(ViolationTypeCategory).map((item) => ({
    value: item,
    label: t(`violation.${item}`),
  }));

  const statusChangeOptions = Object.keys(ViolationTypeStatusChange).map(
    (item) => ({
      value: item,
      label: t(`violation.${item}`),
    }),
  );

  const schema = yup.object().shape({
    name: yup
      .string()
      .max(150, t("validation.more150"))
      .test(
        "name",
        t("validation.required"),
        () => !watch("name") || !!watch("name").trim(),
      )
      .required(t("validation.required")),
    description: yup
      .string()
      .test(
        "description",
        t("validation.required"),
        () => !watch("description") || !!watch("description").trim(),
      )
      .max(500, t("validation.more500"))
      .required(t("validation.required")),
    howToFixDescription: yup
      .string()
      .test(
        "howToFixDescription",
        t("validation.required"),
        () =>
          !watch("howToFixDescription") ||
          !!watch("howToFixDescription").trim(),
      )
      .required(t("validation.required")),
    AvgCorrectionDays: yup
      .number()
      .integer(t("validation.integer"))
      .positive(t("validation.positive"))
      .required(t("validation.required")),
    category: yup.object().nullable().required(t("validation.required")),
    SubdivisionId: yup.object().nullable().required(t("validation.required")),
  });

  const { notifySuccess, notifyError } = useNotify();
  const [setAddViolationTypeMutation] = useAddViolationTypeMutation({
    onCompleted() {
      refetch();
      setCloseModal();
      notifySuccess(t("common.adding_was_successful"));
    },
    onError(vals) {
      notifyError(vals["message"]);
    },
  });
  const [setEditViolationTypeMutation] = useEditViolationTypeMutation({
    onCompleted() {
      refetch();
      setCloseModal();
      notifySuccess(t("common.сhange_was_successful"));
    },
    onError(vals) {
      notifyError(vals["message"]);
    },
  });
  const [setChangeStatusViolationTypeMutation] =
    useChangeStatusViolationTypeMutation({
      onCompleted() {
        refetch();
        setCloseModal();
      },
    });

  const { handleSubmit, control, watch, errors } = useForm({
    defaultValues: {
      ...violation,
      SubdivisionId: SubdivisionId
        ? violation.Subdivision && {
            value: violation.Subdivision.id,
            label: violation.Subdivision.name,
          }
        : undefined,
      category: category
        ? {
            value: category,
            label: t(`violation.${category}`),
          }
        : undefined,
      changeStatus: statusChangeOptions[0],
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = (data) => {
    const {
      name,
      description,
      howToFixDescription,
      category: categoryData,
      SubdivisionId,
      AvgCorrectionDays,
    } = data;

    const baseParams = {
      name,
      description,
      howToFixDescription,
      SubdivisionId: SubdivisionId.value,
      category: ViolationTypeCategory[callEnumVal(categoryData.value)],
      AvgCorrectionDays: Number(AvgCorrectionDays),
    };

    !isEdit
      ? setAddViolationTypeMutation({
          variables: {
            ...baseParams,
          },
        })
      : (setEditViolationTypeMutation({
          variables: {
            id: data.id,
            ...baseParams,
          },
        }),
        setChangeStatusViolationTypeMutation({
          variables: {
            id: data.id,
            status: ViolationTypeStatusChange[data.changeStatus.value],
          },
        }));
  };

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 16) {
      onScrollBottomDivisions();
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        {isEdit && (
          <Field
            type="input"
            name="id"
            control={control}
            label={t("violation.id")}
            disabled
          />
        )}
        <Field
          type="input"
          name="name"
          control={control}
          label={t("violation.nameType")}
          error={errors["name"]?.message}
        />
        <Field
          type="textarea"
          name="description"
          control={control}
          label={t("violation.descriptionType")}
          error={errors["description"]?.message}
        />
        <Field
          type="input"
          name="howToFixDescription"
          control={control}
          label={t("violation.howToFixDescriptionType")}
          error={errors["howToFixDescription"]?.message}
        />
        <Field
          type="select"
          name="category"
          control={control}
          label={t("violation.categoryType")}
          placeholder={t("violation.not_selected")}
          options={categoryOptions}
          labelInValue
          error={errors["category"]?.message}
        />
        <Field
          type="select"
          name="SubdivisionId"
          control={control}
          label={t("violation.SubdivisionId")}
          placeholder={t("violation.not_selected")}
          options={divisionsForSelect}
          labelInValue
          showSearch
          filterOption={false}
          onSearch={searchDivision}
          onPopupScroll={handlePopupScroll}
          loading={GetSubdivisionLoading}
          error={errors["SubdivisionId"]?.message}
        />
        <Field
          type="input"
          name="AvgCorrectionDays"
          control={control}
          label={t("violation.AvgCorrectionDays")}
          error={
            errors["AvgCorrectionDays"]?.type !== "typeError"
              ? errors["AvgCorrectionDays"]?.message
              : t("validation.required")
          }
        />
        {isEdit && (
          <>
            <Field
              type="input"
              name="deletedAt"
              control={control}
              label={t("violation.deletedAt")}
              disabled
            />
            <Field
              type="input"
              name="introducedAt"
              control={control}
              label={t("violation.introducedAt")}
              disabled
            />
            <Field
              type="select"
              name="changeStatus"
              control={control}
              label={t("violation.changeStatus")}
              options={statusChangeOptions}
              labelInValue
              disabled={!hasPermission("violationsTypes.changeStatus")}
            />
          </>
        )}
      </Flex>
    </form>
  );
};

export default ViolationTypesForm;
